import React, { useState } from 'react';
import { 
  Trash2, 
  RotateCcw, 
  AlertTriangle, 
  X 
} from 'lucide-react';

const NotificationFilters = ({ 
  onApplyFilters, 
  projects, 
  notifications = [], 
  onDeleteNotifications,
  onRestoreNotifications,
  onPermanentDelete,
  selectedNotifications = [],
  onSelectNotification,
  onSelectAll,
  showTrash = false,
  onToggleTrash
}) => {
  const [showFilters, setShowFilters] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showPermanentDeleteModal, setShowPermanentDeleteModal] = useState(false);
  const [deleteType, setDeleteType] = useState(''); // 'trash' ou 'permanent'
  const [filters, setFilters] = useState({
    readStatus: 'all', // 'all', 'read', 'unread'
    project: 'all',
    startDate: '',
    endDate: ''
  });
  
  const [activeFilters, setActiveFilters] = useState([]);
  
  const toggleFilters = () => {
    setShowFilters(!showFilters);
  };

  const handleFilterChange = (name, value) => {
    setFilters({
      ...filters,
      [name]: value
    });
  };

  const applyFilters = () => {
    // Create active filters list for chips display
    const newActiveFilters = [];
    
    if (filters.readStatus !== 'all') {
      newActiveFilters.push({
        key: 'readStatus',
        label: `Statut: ${filters.readStatus === 'read' ? 'Lues' : 'Non lues'}`
      });
    }
    
    if (filters.project !== 'all') {
      newActiveFilters.push({
        key: 'project',
        label: `Projet: ${projects.find(p => p.id === filters.project)?.name || filters.project}`
      });
    }
    
    if (filters.startDate && filters.endDate) {
      newActiveFilters.push({
        key: 'dateRange',
        label: `Période: ${filters.startDate} - ${filters.endDate}`
      });
    }
    
    setActiveFilters(newActiveFilters);
    
    // Convert dates to Date objects if present
    const finalFilters = {
      ...filters,
      dateRange: [
        filters.startDate ? new Date(filters.startDate) : null,
        filters.endDate ? new Date(filters.endDate) : null
      ]
    };
    
    onApplyFilters(finalFilters);
    setShowFilters(false);
  };

  const resetFilters = () => {
    const defaultFilters = {
      readStatus: 'all',
      project: 'all',
      startDate: '',
      endDate: ''
    };
    setFilters(defaultFilters);
    setActiveFilters([]);
    onApplyFilters({
      ...defaultFilters,
      dateRange: [null, null]
    });
  };

  const removeFilter = (filterKey) => {
    const newFilters = { ...filters };
    
    if (filterKey === 'dateRange') {
      newFilters.startDate = '';
      newFilters.endDate = '';
    } else {
      newFilters[filterKey] = 'all';
    }
    
    setFilters(newFilters);
    
    // Update active filters
    const newActiveFilters = activeFilters.filter(filter => filter.key !== filterKey);
    setActiveFilters(newActiveFilters);
    
    // Apply updated filters
    onApplyFilters({
      ...newFilters,
      dateRange: filterKey === 'dateRange' ? [null, null] : [
        newFilters.startDate ? new Date(newFilters.startDate) : null,
        newFilters.endDate ? new Date(newFilters.endDate) : null
      ]
    });
  };

  // Gestion de la suppression
  const handleDeleteClick = (type) => {
    setDeleteType(type);
    if (type === 'permanent') {
      setShowPermanentDeleteModal(true);
    } else {
      setShowDeleteModal(true);
    }
  };

  const confirmDelete = () => {
    if (deleteType === 'permanent') {
      onPermanentDelete(selectedNotifications);
      setShowPermanentDeleteModal(false);
    } else {
      onDeleteNotifications(selectedNotifications);
      setShowDeleteModal(false);
    }
    setDeleteType('');
  };

  const handleRestore = () => {
    onRestoreNotifications(selectedNotifications);
  };

  const someSelected = selectedNotifications.length > 0;

  return (
    <div className="mb-4">
      {/* Barre d'actions simplifiée - SANS le bouton sélectionner tout en double */}
      <div className="flex justify-between items-center mb-4 bg-white p-4 rounded-lg shadow-sm">
        <div className="flex items-center space-x-4">
          {/* Actions sur les éléments sélectionnés */}
          {someSelected && (
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-600">
                {selectedNotifications.length} sélectionnée{selectedNotifications.length > 1 ? 's' : ''}
              </span>
              
              {!showTrash ? (
                <button
                  onClick={() => handleDeleteClick('trash')}
                  className="flex items-center space-x-1 px-3 py-1 text-red-600 hover:bg-red-50 rounded"
                >
                  <Trash2 className="w-4 h-4" />
                  <span className="text-sm">Mettre à la corbeille</span>
                </button>
              ) : (
                <div className="flex space-x-2">
                  <button
                    onClick={handleRestore}
                    className="flex items-center space-x-1 px-3 py-1 text-green-600 hover:bg-green-50 rounded"
                  >
                    <RotateCcw className="w-4 h-4" />
                    <span className="text-sm">Restaurer</span>
                  </button>
                  <button
                    onClick={() => handleDeleteClick('permanent')}
                    className="flex items-center space-x-1 px-3 py-1 text-red-600 hover:bg-red-50 rounded"
                  >
                    <Trash2 className="w-4 h-4" />
                    <span className="text-sm">Supprimer définitivement</span>
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center space-x-2">
          {/* Basculer entre normal et corbeille */}
          <button
            onClick={onToggleTrash}
            className={`px-3 py-1 rounded text-sm ${
              showTrash 
                ? 'bg-gray-200 text-gray-700' 
                : 'border border-gray-300 text-gray-600 hover:bg-gray-50'
            }`}
          >
            {showTrash ? 'Retour aux notifications' : 'Corbeille'}
          </button>

          {/* Bouton filtres */}
          <button 
            className="px-3 py-1 border border-blue-500 text-blue-500 rounded hover:bg-blue-50 focus:outline-none text-sm"
            onClick={toggleFilters}
          >
            {showFilters ? 'Masquer les filtres' : 'Filtrer'}
          </button>
        </div>
      </div>

      {/* Filtres actifs */}
      {activeFilters.length > 0 && (
        <div className="flex flex-wrap mb-4">
          {activeFilters.map((filter, index) => (
            <span 
              key={index} 
              className="bg-gray-100 text-gray-800 rounded-full px-3 py-1 text-sm mr-2 mb-2 flex items-center"
            >
              {filter.label}
              <button 
                type="button" 
                className="ml-2 text-gray-500 hover:text-gray-700 focus:outline-none"
                onClick={() => removeFilter(filter.key)}
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}
      
      {/* Panel de filtres */}
      {showFilters && (
        <div className="bg-white shadow rounded-lg p-4 mb-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label htmlFor="readStatus" className="block text-sm font-medium text-gray-700 mb-1">
                Statut de lecture
              </label>
              <select 
                id="readStatus"
                className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                value={filters.readStatus}
                onChange={(e) => handleFilterChange('readStatus', e.target.value)}
              >
                <option value="all">Toutes</option>
                <option value="read">Lues</option>
                <option value="unread">Non lues</option>
              </select>
            </div>
            
            <div>
              <label htmlFor="project" className="block text-sm font-medium text-gray-700 mb-1">
                Projet
              </label>
              <select 
                id="project"
                className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                value={filters.project}
                onChange={(e) => handleFilterChange('project', e.target.value)}
              >
                <option value="all">Tous les projets</option>
                {projects.map((project) => (
                  <option key={project.id} value={project.id}>
                    {project.name}
                  </option>
                ))}
              </select>
            </div>
            
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label htmlFor="startDate" className="block text-sm font-medium text-gray-700 mb-1">
                  Date début
                </label>
                <input
                  type="date"
                  id="startDate"
                  className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  value={filters.startDate}
                  onChange={(e) => handleFilterChange('startDate', e.target.value)}
                />
              </div>
              <div>
                <label htmlFor="endDate" className="block text-sm font-medium text-gray-700 mb-1">
                  Date fin
                </label>
                <input
                  type="date"
                  id="endDate"
                  className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  value={filters.endDate}
                  onChange={(e) => handleFilterChange('endDate', e.target.value)}
                />
              </div>
            </div>
          </div>
          
          <div className="flex justify-end mt-4">
            <button 
              type="button" 
              className="text-gray-600 hover:text-gray-800 mr-3 text-sm"
              onClick={resetFilters}
            >
              Réinitialiser
            </button>
            <button 
              type="button" 
              className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 text-sm"
              onClick={applyFilters}
            >
              Appliquer
            </button>
          </div>
        </div>
      )}

      {/* Modal de confirmation - Corbeille */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center space-x-3 mb-4">
              <AlertTriangle className="w-6 h-6 text-orange-500" />
              <h3 className="text-lg font-semibold text-gray-900">
                Mettre à la corbeille
              </h3>
            </div>
            <p className="text-gray-600 mb-6">
              Êtes-vous sûr de vouloir mettre {selectedNotifications.length} notification{selectedNotifications.length > 1 ? 's' : ''} à la corbeille ? 
              Vous pourrez les restaurer plus tard.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Annuler
              </button>
              <button
                onClick={confirmDelete}
                className="px-4 py-2 bg-orange-500 text-white rounded hover:bg-orange-600"
              >
                Mettre à la corbeille
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de confirmation - Suppression définitive */}
      {showPermanentDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center space-x-3 mb-4">
              <AlertTriangle className="w-6 h-6 text-red-500" />
              <h3 className="text-lg font-semibold text-gray-900">
                Suppression définitive
              </h3>
            </div>
            <p className="text-gray-600 mb-6">
              <strong>Attention :</strong> Cette action est irréversible. 
              Êtes-vous sûr de vouloir supprimer définitivement {selectedNotifications.length} notification{selectedNotifications.length > 1 ? 's' : ''} ?
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowPermanentDeleteModal(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Annuler
              </button>
              <button
                onClick={confirmDelete}
                className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
              >
                Supprimer définitivement
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationFilters;